/**
 * Double-submit CSRF helper.
 *
 * The backend issues a `fias_csrf` cookie alongside every successful
 * login / refresh. The cookie is intentionally *not* httpOnly so we
 * can read it here and echo the value as `X-CSRF-Token` on every
 * state-changing request. The backend compares header against cookie
 * in constant time and rejects a mismatch with 403.
 *
 * Why double-submit instead of relying on SameSite alone:
 *   The platform deploys cross-origin (frontend on its own domain,
 *   API on another). That forces SameSite=None on auth cookies, which
 *   disables the browser's built-in CSRF protection. The custom-header
 *   approach plugs the gap — a hostile origin cannot read our CSRF
 *   cookie (different origin) and cannot send a custom header on a
 *   cross-origin form submission without a CORS preflight, which our
 *   allowlist rejects.
 *
 * GET / HEAD / OPTIONS skip CSRF (they're idempotent). The auth
 * endpoints that bootstrap a session (login, signup, refresh) also
 * skip it server-side because the user has no cookie yet.
 */

const CSRF_COOKIE = 'fias_csrf';

/**
 * Cached CSRF value. Two sources:
 *   - Login / refresh / dev-login responses include `csrfToken` in
 *     their JSON body, which we capture via `setCsrfToken`. This is
 *     the fast path — no document.cookie parse needed.
 *   - On page reload we re-hydrate from the document.cookie value,
 *     since the in-memory cache is gone. Done lazily via `readCookie`.
 */
let cachedToken: string | null = null;

function readCookie(): string | null {
  if (typeof document === 'undefined' || !document.cookie) return null;
  const prefix = `${CSRF_COOKIE}=`;
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) {
      return decodeURIComponent(trimmed.slice(prefix.length));
    }
  }
  return null;
}

export function setCsrfToken(token: string | null): void {
  cachedToken = token;
}

export function getCsrfToken(): string | null {
  if (cachedToken) return cachedToken;
  cachedToken = readCookie();
  return cachedToken;
}

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

/**
 * Mutates `headers` to include `X-CSRF-Token` when the request method
 * is unsafe and a CSRF token is available. Safe verbs are left alone
 * — the backend skips CSRF on them anyway, and adding the header
 * would force a CORS preflight on every GET.
 */
export function attachCsrfHeader(method: string, headers: Record<string, string>): void {
  if (SAFE_METHODS.has(method.toUpperCase())) return;
  const token = getCsrfToken();
  if (token) {
    headers['X-CSRF-Token'] = token;
  }
}
